import React, { useEffect, useMemo, useState } from 'react'
import Breadcrumbs from 'components/Breadcrumbs'
import { Button, Col, Grid, Icon, InputBase, Typography } from 'gu-ui-kit'
import { useDispatch, useSelector } from 'react-redux'
import { ROUTES } from 'constants/routes'
import { useParams } from 'react-router'
import { FETCH_PRODUCT } from 'modules/AccountsMarketplace/Product/types'
import { useTranslation } from 'react-i18next'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Helmet } from 'react-helmet'
import styles from 'modules/AccountsMarketplace/Product/product.module.scss'
import clsx from 'clsx'
import formatMoney from 'utils/formatMoney'
import Decimal from 'decimal.js'
import { NumericFormat } from 'react-number-format'
import { createPath } from 'history'
import { Link } from 'react-router-dom'
import qs from 'qs'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import GridSpinner from 'components/UI/GridSpinner'
import { useGetCategory } from 'modules/AccountsMarketplace/hooks/useGetCategory'
import he from 'he'

const AccountsMarketplaceProduct = () => {
  const { productId } = useParams()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const breakpoints = useBreakpoint()
  const [amountValue, setAmountValue] = useState(1)
  const { category } = useGetCategory(category_id)

  const { category_id, name, price, amount, currency, description, id, loading } = useSelector(
    ({ ['accounts-marketplace/product']: product }) => ({
      ...product,
    }),
  )

  useEffect(
    () => productId && dispatch({ type: FETCH_PRODUCT, payload: { id: productId } }),
    [productId],
  )

  const breadCrumbsConfig = useMemo(
    () => [
      {
        title: t('accounts_marketplace::title'),
        to: pathGen(ROUTES.ACCOUNTS_MARKETPLACE),
      },
      {
        title: category?.name,
        to: pathGen(ROUTES.ACCOUNTS_MARKETPLACE_CATEGORY, {
          categoryId: category_id,
        }),
      },
      {
        title: t('accounts_marketplace::breadcrumbs.buy_account'),
        to: pathGen(ROUTES.ACCOUNTS_MARKETPLACE_CATEGORY_PRODUCTS, {
          categoryId: category_id,
        }),
      },
      {
        title: t('accounts_marketplace::breadcrumbs.account_type', {
          name: category?.name,
        }),
      },
    ],
    [category_id, category?.name, t],
  )

  return (
    <>
      <Helmet>
        <title>
          {`${t('accounts_marketplace::navigation.product')} — ${t('accounts_marketplace::title')}`}
        </title>
      </Helmet>
      <Grid gap={24} direction={'column'} className={'margin-bottom-24'}>
        <Breadcrumbs links={breadCrumbsConfig} className={'margin-bottom-0'} />
      </Grid>
      <Grid
        gap={16}
        className={clsx(styles['pad-container'], {
          'margin-bottom-32': breakpoints.fb,
          'margin-bottom-40': !breakpoints.fb,
        })}
      >
        <Col flexGrow={1} fb="6" l="8" className={styles['pad']}>
          <Grid gap={16}>
            <Grid gap={16} direction={'column'}>
              <Typography variant={'Header-50'}>{category?.name}</Typography>
              <Typography variant={'Body-Accent-20'}>{he.decode(name)}</Typography>
            </Grid>
            {breakpoints.fbs && (
              <>
                {loading ? (
                  <GridSpinner width={40} />
                ) : (
                  <div>
                    <Typography
                      variant={'Body-Accent-80'}
                      component={
                        <span
                          className={styles['price']}
                          dangerouslySetInnerHTML={{
                            __html: formatMoney(price, currency),
                          }}
                        />
                      }
                    />

                    <Typography variant={'Accent-20'} color={'Label-Tertiary'}>
                      {' / '}
                      {t('accounts_marketplace::product.right-pad.per_item')}
                    </Typography>
                  </div>
                )}
                <Grid wrap={false}>
                  <Col flexGrow={0}>
                    <Button
                      component={Link}
                      disabled={!amountValue}
                      size={'l'}
                      variant={'primary'}
                      to={createPath({
                        pathname: pathGen(ROUTES.BILLING_MARKETPLACE_PRODUCT_PAYMENT, {
                          productId: id,
                        }),
                        search: qs.stringify({
                          quantity: amountValue,
                        }),
                      })}
                    >
                      {t('accounts_marketplace::product.buy_button')}
                    </Button>
                  </Col>
                  <Col flexGrow={1}>
                    <Grid wrap={false} gap={16} fluid justifyContent={'flex-end'}>
                      <Button
                        onClick={() => setAmountValue((prev) => prev - 1)}
                        disabled={amountValue === 1}
                        grey
                        variant={'secondary'}
                        color={'Label-Quaternary'}
                        size={'l'}
                        startIcon={<Icon name={'minus'} />}
                        noLabel
                      />
                      <div className={styles['amount-input']}>
                        <NumericFormat
                          className={styles['amount-input__editable']}
                          value={amountValue}
                          onChange={({ currentTarget: { value } }) => {
                            setAmountValue(!value ? 1 : parseInt(value.trim().replace(/-|\s/g, '')))
                          }}
                          isAllowed={({ value }) => {
                            if (!value) {
                              return true
                            } else {
                              const floatVal = new Decimal(value)
                              return floatVal.greaterThan('0') && floatVal.lessThanOrEqualTo(amount)
                            }
                          }}
                          placeholder={1}
                          customInput={InputBase}
                          thousandSeparator={' '}
                          allowLeadingZeros={true}
                          decimalScale={0}
                          fixedDecimalScale
                          size={'m'}
                          style={{
                            maxWidth: (amountValue ? amountValue.toString().length : 1) * 10 + 'px',
                          }}
                        />

                        <Typography variant={'Body-Forms-20'} color={'Label-Quaternary'}>
                          /{amount}
                        </Typography>
                      </div>
                      <Button
                        disabled={amountValue === amount}
                        onClick={() => setAmountValue((prev) => prev + 1)}
                        grey
                        variant={'secondary'}
                        color={'Label-Quaternary'}
                        size={'l'}
                        startIcon={<Icon name={'plus'} />}
                        noLabel
                      />
                    </Grid>
                  </Col>
                </Grid>
              </>
            )}
          </Grid>
        </Col>

        {!breakpoints.fbs && (
          <Col flexGrow={0} fb="6" l="4" className={clsx(styles['pad'], styles['pad__right'])}>
            <Grid gap={16} direction={'column'}>
              <Typography
                variant={'Body-15'}
                component={'div'}
                className={clsx('margin-top-16', 'margin-bottom-16')}
              >
                {t('accounts_marketplace::product.right-pad.buy_account')}
              </Typography>
              {loading ? (
                <GridSpinner width={40} />
              ) : (
                <div>
                  <Typography
                    variant={'Body-Accent-80'}
                    component={
                      <span
                        className={styles['price']}
                        dangerouslySetInnerHTML={{
                          __html: formatMoney(price, currency),
                        }}
                      />
                    }
                  />

                  <Typography variant={'Accent-20'} color={'Label-Tertiary'}>
                    {' / '}
                    {t('accounts_marketplace::product.right-pad.per_item')}
                  </Typography>
                </div>
              )}
              <Grid wrap={false}>
                <Col flexGrow={0}>
                  <Button
                    component={Link}
                    disabled={!amountValue}
                    size={'l'}
                    variant={'primary'}
                    to={createPath({
                      pathname: pathGen(ROUTES.BILLING_MARKETPLACE_PRODUCT_PAYMENT, {
                        productId: id,
                      }),
                      search: qs.stringify({
                        quantity: amountValue,
                      }),
                    })}
                  >
                    {t('accounts_marketplace::product.buy_button')}
                  </Button>
                </Col>
                <Col flexGrow={1}>
                  <Grid wrap={false} gap={16} fluid justifyContent={'flex-end'}>
                    <Button
                      onClick={() => setAmountValue((prev) => prev - 1)}
                      disabled={amountValue === 1}
                      grey
                      variant={'secondary'}
                      color={'Label-Quaternary'}
                      size={'l'}
                      startIcon={<Icon name={'minus'} />}
                      noLabel
                    />
                    <div className={styles['amount-input']}>
                      <NumericFormat
                        className={styles['amount-input__editable']}
                        value={amountValue}
                        onChange={({ currentTarget: { value } }) => {
                          setAmountValue(!value ? 1 : parseInt(value.trim().replace(/-|\s/g, '')))
                        }}
                        isAllowed={({ value }) => {
                          if (!value) {
                            return true
                          } else {
                            const floatVal = new Decimal(value)
                            return floatVal.greaterThan('0') && floatVal.lessThanOrEqualTo(amount)
                          }
                        }}
                        placeholder={1}
                        customInput={InputBase}
                        thousandSeparator={' '}
                        allowLeadingZeros={true}
                        decimalScale={0}
                        fixedDecimalScale
                        size={'m'}
                        style={{
                          maxWidth: (amountValue ? amountValue.toString().length : 1) * 10 + 'px',
                        }}
                      />

                      <Typography variant={'Body-Forms-20'} color={'Label-Quaternary'}>
                        /{amount}
                      </Typography>
                    </div>
                    <Button
                      disabled={amountValue === amount}
                      onClick={() => setAmountValue((prev) => prev + 1)}
                      grey
                      variant={'secondary'}
                      color={'Label-Quaternary'}
                      size={'l'}
                      startIcon={<Icon name={'plus'} />}
                      noLabel
                    />
                  </Grid>
                </Col>
              </Grid>
            </Grid>
          </Col>
        )}
      </Grid>
      <Grid gap={16}>
        <Col flexGrow={1} l="8">
          <div
            dangerouslySetInnerHTML={{
              __html: he.decode(description).replace(/\n/gm, '<br />'),
            }}
          />
        </Col>
        <Col flexGrow={0} l={'4'} className={styles['pad__right']} />
      </Grid>
    </>
  )
}

export default AccountsMarketplaceProduct
